import * as Sentry from '@sentry/react';
import {
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { ImsApi } from '@/api/ImsApi.ts';
import { isCountryRecognized, isRegionBlocked } from '@/auth/allowedCountries';
import { ImsApiContext } from '@/auth/ImsApiContext.tsx';
import {
	clearCachedImsAuthId,
	getCachedImsAuthId,
	getProxyAuthHeaders,
	setCachedImsAuthId,
} from '@/auth/imsTokenStorage';
import {
	clearForcedRegionBlock,
	FORCED_REGION_BLOCK_COUNTRY,
	isRegionBlockForced,
} from '@/auth/regionBlockOverride';
import { config } from '@/config/env';
import { logger, setLogUserId } from '@/logger';
import { identifyUser } from '@/utils/fullstory';

// Ask the ui-proxy for the caller's country, derived server-side from the
// request IP (the browser can't see its own public-IP geo). Returns the code,
// or null on any non-ok response or network error. The request carries the
// caller's IMS token (getProxyAuthHeaders): in deployed environments the
// ui-proxy sits behind the Ethos CGW gateway, whose IMS auth policy rejects
// every unauthenticated path except /ping with a 403 — without the token the
// call never reaches the handler, so the region gate silently fails open
// (countryCode: null) and never enforces. Every failure path still returns
// null so the region gate fails open: an unreachable endpoint must let the
// login proceed, never lock the user out.
const fetchClientRegion = async (
	accessToken: string | null,
): Promise<string | null> => {
	try {
		const response = await fetch(`${config.UI_PROXY_URL}/client-region`, {
			headers: getProxyAuthHeaders(accessToken),
		});
		if (!response.ok) {
			return null;
		}
		const json = await response.json();
		return json.countryCode ?? null;
	} catch {
		return null;
	}
};

// Fetch this user's feature flags from ui-proxy. Fails closed to `{}` on any error so a
// ui-proxy problem hides in-progress features rather than leaking them. Timeout-bounded:
// it's on the login critical path, and the fail-closed catch handles a returned error but not a hang.
const FEATURE_FLAGS_TIMEOUT_MS = 4000;
const fetchFeatureFlags = async (
	userId: string,
	accessToken: string | null,
): Promise<Record<string, boolean>> => {
	const controller = new AbortController();
	const timeout = setTimeout(
		() => controller.abort(),
		FEATURE_FLAGS_TIMEOUT_MS,
	);
	try {
		const response = await fetch(
			`${config.UI_PROXY_URL}/feature-flags?userId=${encodeURIComponent(userId)}`,
			{ headers: getProxyAuthHeaders(accessToken), signal: controller.signal },
		);
		if (!response.ok) {
			return {};
		}
		const json = await response.json();
		return json.flags ?? {};
	} catch {
		return {};
	} finally {
		clearTimeout(timeout);
	}
};

export const ImsApiProvider = ({
	children,
}: {
	children: ReactNode;
}): ReactElement => {
	const [imsApi] = useState(() => new ImsApi());
	const [userId, setUserId] = useState<string | null>(null);
	const [imsToken, setImsToken] = useState<string | null>(null);
	const [deniedEmail, setDeniedEmail] = useState<string | null>(null);
	const [blockedCountry, setBlockedCountry] = useState<string | null>(null);
	const [featureFlags, setFeatureFlags] = useState<Record<
		string,
		boolean
	> | null>(null);
	const [loading, setLoading] = useState(true);

	const expireSession = useCallback(() => {
		imsApi.expireSession();
		clearCachedImsAuthId();
		setUserId(null);
		setImsToken(null);
		setDeniedEmail(null);
		setBlockedCountry(null);
		// Sign-in stamps the user onto every browser log line. Clear it here or the
		// next person to use this tab has their lines attributed to the account that
		// just expired. This callback is the one funnel every expiry path reaches.
		setLogUserId('');
	}, [imsApi]);

	/**
	 * Undo the early account bind when the product refuses the login.
	 *
	 * The bind below runs before the region gate and the allowlist gate, so a
	 * popup failure inside that window is not anonymous. A refusal must leave
	 * nothing behind. `setCachedImsAuthId` writes to localStorage, which
	 * outlives the tab, and `sentry-init.ts` re-reads that key and calls
	 * `setUser` at every later boot. The interstitial's own Sign out does not
	 * help: `imsApi.logout()` clears only the token and expiry keys.
	 *
	 * Sentry is unbound here too, because the effect that mirrors `userId` keys
	 * off a value neither refusal path ever changes.
	 */
	const releaseBoundAccount = useCallback(() => {
		clearCachedImsAuthId();
		Sentry.setUser(null);
	}, []);

	useEffect(() => {
		const initialize = async () => {
			// QA override from the State Viewer: treat this one login as an
			// account from a blocked region so the interstitial can be reviewed
			// on demand. Read synchronously BEFORE the first await: React
			// StrictMode (dev) double-fires this effect, and both runs'
			// synchronous prefixes execute before either resumes past an await —
			// so both reads see the flag armed, both block, and the clear at the
			// commit point stays idempotent. Reading after the profile fetch
			// would let run 1 clear the flag before run 2 reads it, and run 2
			// would complete a real login behind the interstitial.
			const forced = isRegionBlockForced();
			try {
				// If we are coming from an auth callback, cache the token
				imsApi.cacheTokenFromAuthCallbackUrl();

				// Now, check if we have a valid token cached.
				// If we do, fetch the user profile and put the user ID and token into the ImsApiContext
				//  to allow the user to proceed past the AuthGate.
				// If we do not, AuthGate will show the landing page.
				if (imsApi.isCachedTokenValid()) {
					const accessToken = imsApi.getCachedAccessToken();
					const profile = await imsApi.fetchImsProfileOrRedirect(
						accessToken,
						false,
					);

					// Stamp the logger as soon as the identity is known, not at the
					// commit point below. The region and allowlist checks between here
					// and there each write a log line, and those lines described a user
					// the search could not find. A blocked or denied account still gets
					// its lines attributed, which is where support needs them most.
					if (profile) {
						setLogUserId(profile.authId);
					}

					// Beacon is only offered for US/Canada accounts. The country
					// is resolved from the caller's IP by the ui-proxy, not from
					// the IMS profile. Accounts from blocked regions stop here: no
					// allowlist call, no userId/token published to context (so the
					// bridge WebSocket never connects), no FullStory identify. Two
					// backend contacts are expected while blocked: the
					// /client-region lookup that produces the code (it IS the
					// blocking mechanism), and the "Region blocked" log line below
					// — a POST to the ui-proxy Splunk relay whose body carries only
					// the country code, never the email. Blocking requires the
					// code to be a recognized ISO code — missing, malformed, or
					// unrecognized values fail open (see allowedCountries.ts).
					if (profile) {
						// Bind the account now, not at the end. Four more
						// requests run before userId is published, and a popup
						// failure inside that window would be anonymous.
						setCachedImsAuthId(profile.authId);
						Sentry.setUser({ id: profile.authId });

						const countryCode = forced
							? FORCED_REGION_BLOCK_COUNTRY
							: await fetchClientRegion(accessToken);
						if (!countryCode) {
							// A null region (endpoint down, private IP, unresolved
							// lookup) must let the login proceed rather than lock the
							// user out, so flag the gap in Splunk and continue. The
							// authId lets support tie a user's report to this case;
							// it is the only personal identifier this warning
							// carries.
							logger.warn('Client region unavailable', {
								authId: profile.authId,
							});
						} else if (isRegionBlocked(countryCode)) {
							// Consume the one-shot override at the commit point, not
							// when reading it: this is idempotent across StrictMode's
							// two runs (both block, both clear) and only fires once
							// the interstitial is actually shown.
							if (forced) {
								clearForcedRegionBlock();
							}
							logger.info('Region blocked', {
								countryCode,
								...(forced ? { forced: true } : {}),
							});
							// The interstitial was built to publish no identity
							// anywhere, so the account id must not survive it.
							releaseBoundAccount();
							setBlockedCountry(countryCode);
							return;
						} else if (!isCountryRecognized(countryCode)) {
							// A value that isn't any ISO country code we know lets
							// the login proceed — blocking on values we can't
							// positively identify is what got the first rollout of
							// this gate reverted. Warn so Splunk shows exactly which
							// values are flowing through fail-open.
							logger.warn('Unrecognized client region', {
								countryCode,
								authId: profile.authId,
							});
						}
					}

					if (profile && profile.email) {
						const response = await fetch(
							`${config.UI_PROXY_URL}/is-allowlisted-user?userEmail=${encodeURIComponent(profile.email)}`,
							{
								headers: getProxyAuthHeaders(accessToken),
							},
						);

						if (!response.ok) {
							// The allowlist check failed (not a denial — a transport
							// or proxy error). Still not an authorized account, so the
							// early bind above must not survive this session.
							logger.error('Failed to fetch allowlisted users', {
								status: response.status,
								statusText: response.statusText,
							});
							releaseBoundAccount();
							return;
						}

						const jsonResponse = await response.json();
						if (jsonResponse.allowlisted) {
							// Resolve flags before publishing userId so the gated app renders
							// with flags in hand (no flash of a feature that then disappears).
							const flags = await fetchFeatureFlags(
								profile.authId,
								accessToken,
							);
							setFeatureFlags(flags);
							setUserId(profile.authId);
							setImsToken(accessToken);
							identifyUser(profile.authId, {
								displayName: profile.displayName || profile.name,
								email: profile.email,
							});
						} else {
							// The allowlist refused the account. The same rule
							// applies as for a blocked region: publish no identity
							// for an account the product will not serve.
							releaseBoundAccount();
							setDeniedEmail(profile.email);
						}
					} else if (profile) {
						// No email on the profile: the allowlist gate above never
						// runs, so nothing here will ever call setUserId. The early
						// bind must not survive an account that never clears the
						// gate.
						releaseBoundAccount();
					}
				} else {
					expireSession();
				}
			} finally {
				setLoading(false);
			}
		};

		void initialize();
	}, [expireSession, imsApi, releaseBoundAccount]);

	// This provider mounts above every route, including the OAuth popup route,
	// which AuthGate does not wrap. Binding the user here is what gives a popup
	// error event an account to trace it to.
	useEffect(() => {
		// Fall back to the cached id. This effect also runs on mount, when
		// userId is still null, and clearing there would drop the identity that
		// initSentry bound before React rendered.
		const boundId = userId ?? getCachedImsAuthId();
		if (boundId) {
			Sentry.setUser({ id: boundId });
		} else {
			Sentry.setUser(null);
		}
	}, [userId]);

	useEffect(() => {
		const handleFocus = () => {
			if (imsToken && !imsApi.isCachedTokenValid()) {
				expireSession();
			}
		};
		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, [expireSession, imsApi, imsToken]);

	const sessionDeadline = imsToken ? imsApi.getSessionDeadline() : null;

	return (
		<ImsApiContext.Provider
			value={{
				imsApi,
				userId,
				imsToken,
				sessionDeadline,
				expireSession,
				deniedEmail,
				loading,
				blockedCountry,
				featureFlags,
			}}
		>
			{children}
		</ImsApiContext.Provider>
	);
};
